<template>
    <div class="w-full space-y-3">
        <Head title="Credit Card Reconciliation"/>
        <div class="flex justify-between">
            <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">Expenses For Review</h1>
        </div>
        <SlideOutErrors :show="showErrors === true"/>
    </div>
    <div class="bg-white py-6 px-4 sm:p-6 shadow mb-6 mt-6">
        <div class="flex flex-col mt-3 space-y-3">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Team
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Date
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Posted Date
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Merchant
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Category
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Amount
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        GBP Amount
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Name on Card
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        Last 4 Card Digits
                                    </th>
                                    <th scope="col"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="expenses.data.length > 0" v-for="(expense, index) in expenses.data"
                                    :key="index"
                                    :class="{'bg-gray-100': index % 2, 'bg-red-100 hover:bg-red-50' : expense.duplicate_expense_id > 0}" class="hover:bg-gray-50 cursor-pointer">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ expense.team }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ moment(expense.date, 'YYYY-MM-DD').format('DD MMM YYYY') }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ moment(expense.posted_date, 'YYYY-MM-DD').format('DD MMM YYYY') }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ expense.merchant }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ expense.category }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="expense.currency">
                                            {{ formatCurrency(expense.original_currency_amount, expense.currency.code, true) }}
                                        </span>
                                        <span v-else>
                                            {{ expense.amount }}
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <span v-if="expense.currency">
                                            {{ formatCurrency(expense.amount, 'GBP', true) }}
                                        </span>
                                        <span v-else>
                                            {{ expense.amount }}
                                        </span>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ expense.card?.name }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ expense.card?.card_number }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <button type="button" @click="assignExpenseToFixture(expense)" title="Assign To Fixture">
                                            <PencilAltIcon class="w-5 h-5 text-light-pitch-purple"></PencilAltIcon>
                                        </button>
                                    </td>
                                </tr>
                                <tr v-else>
                                    <td colspan="2"
                                        class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        There are no expenses to review.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <Pagination :links="expenses.links"/>
        </div>
    </div>
    <AssignExpenseToFixture ref="assign_expense"/>
</template>

<script>
import {ref, computed} from "vue";
import moment from "moment/moment";
import debounce from "lodash/debounce";
import Pagination from "@/Components/Pagination";
import Authenticated from "@/Layouts/Authenticated";
import SearchFilter from "@/Components/SearchFilter";
import {Head, useForm, usePage} from "@inertiajs/vue3";
import SlideOutErrors from "@/Components/SlideOutErrors";
import {PencilAltIcon, CheckCircleIcon} from '@heroicons/vue/solid'
import AssignExpenseToFixture from "@/Components/SlideOuts/AssignExpenseToFixture";

export default {
    layout: Authenticated,
    components: {
        Head,
        Pagination,
        SearchFilter,
        PencilAltIcon,
        SlideOutErrors,
        CheckCircleIcon,
        AssignExpenseToFixture
    },
    props: ['expenses', 'fixtures', 'transaction_types'],
    data() {
        return {
            form: useForm({
                search: null
            })
        }
    },
    setup(props) {
        const form = useForm({
            file_uploads: []
        });

        const showErrors = ref(false);

        return {
            form,
            showErrors
        }
    },
    methods: {
        prepareFileUpload(event) {
            this.form.file_uploads = event.target.files
        },
        submit() {

            this.form.post(route('frontend.credit-card-reconciliation.upload'), {
                onSuccess: response => {
                    this.showErrors = false;
                    this.form.reset();
                },
                onError: errors => {
                    this.showErrors = true;
                }
            });
        },
        assignExpenseToFixture(expense) {
            this.$refs.assign_expense.id = expense.id;
            this.$refs.assign_expense.amount = expense.original_currency_amount;
            this.$refs.assign_expense.currency_code = expense.currency.code;
            this.$refs.assign_expense.gbp_amount = expense.amount;
            this.$refs.assign_expense.merchant = expense.merchant;
            this.$refs.assign_expense.date = expense.date;
            this.$refs.assign_expense.duplicate_expense = expense.duplicate_expense;
            this.$refs.assign_expense.open();
        },
        close() {
            this.show = false;
        },
        duplicateExpense(id) {
            this.$swal.fire({
                title: 'Mark Expense As Duplicate',
                html: "Marking this expense as a duplicate will remove it from the currently shown list.<br/><br/>" +
                    "Are you sure you wish to do this?",
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No',
                stopKeydownPropagation: true,
                keydownListenerCapture: true
            })
            .then((result) => {
                if (result.isConfirmed) {
                    // Trigger the loader
                    this.loading = true;
                    
                    axios.post(route('frontend.expenses-for-review.duplicate_expense', { id: id })).then(response => {
                        if (response.data.message) {
                            // Need to show the message
                            this.loading = false;
                        } else if (response.data.error) {
                            this.showErrors = true;
                            this.loading = false;
                            // Need to show the message
                        }

                        // Reload the page
                        location.reload();
                    });
                }
            });
        }
    },
    created() {
        this.moment = moment
    }
}
</script>
